import type {
  GeometryLineageResponse,
  GeometryStatus,
  MeshResponse,
} from '@/api/generated/types.gen';
import { statusInProgress } from '@/lib/statusTone';
import { lineageTipId } from '@/utils/lineageTip';

/**
 * Per-geometry state for the draft Mesh step.
 *
 * The mesh-derived four, plus what the GEOMETRY itself is doing (ENG-3100).
 * A geometry uploaded from a study is diagnosed, repaired or simplified
 * before it can be meshed; read from the mesh list alone, all of that showed
 * as "Not started".
 * - `loading` — neither list has answered yet.
 * - `processing` — a version is being diagnosed / repaired / extracted.
 * - `awaiting_approval` — a simplify proposal waits on the reader.
 * - `geometry_error` — no version of it can be meshed.
 */
export type GeometryMeshingStatus =
  | 'loading'
  | 'processing'
  | 'awaiting_approval'
  | 'geometry_error'
  | 'not_started'
  | 'meshing'
  | 'completed'
  | 'failed';

/**
 * What each status asks of the reader on the Mesh step — the one table the
 * readiness reasons, the draft chrome and the tree's action button read, so a
 * new status is a compile error here rather than a branch missed elsewhere.
 * - `done` — meshed.
 * - `wait` — the backend is still preparing it; nothing to do yet.
 * - `review` — the reader has to look at it in the workspace first.
 * - `mesh` — ready for (another) mesh.
 * - `unknown` — not reported yet.
 */
export type MeshStepAsk = 'done' | 'wait' | 'review' | 'mesh' | 'unknown';
export const MESH_STEP_ASK: Record<GeometryMeshingStatus, MeshStepAsk> = {
  loading: 'unknown',
  processing: 'wait',
  awaiting_approval: 'review',
  geometry_error: 'review',
  not_started: 'mesh',
  meshing: 'mesh',
  completed: 'done',
  failed: 'mesh',
};

/**
 * The status of a geometry as the draft holds it — the uploaded ROOT, while
 * repair and simplify run on child versions and leave the root `idle`. So
 * read the whole lineage: any version still in flight, then a proposal
 * waiting on the reader, then the version MeshGen would mesh.
 */
export function lineageGeometryStatus(
  lineage: GeometryLineageResponse | undefined,
): GeometryStatus | undefined {
  const versions = lineage?.versions;
  if (!versions?.length) return undefined;
  const busy = versions.find((v) => statusInProgress('geometry', v.status));
  if (busy) return busy.status;
  if (versions.some((v) => v.status === 'awaiting_approval'))
    return 'awaiting_approval';
  const tipId = lineageTipId(lineage);
  return versions.find((v) => v.id === tipId)?.status ?? 'error';
}

/** How long after upload, or after a diagnosis, a quiet lineage may still
 *  change: the diagnose request lands a moment after the upload returns, and
 *  auto-repair spawns its version a moment after the diagnosis. */
const SETTLE_MS = 60_000;

/**
 * Poll interval for a lineage query, or false. Polls while any version is in
 * flight, and for a minute after the newest one was created or diagnosed —
 * a just-uploaded geometry reads `idle` until its diagnosis starts, and one
 * just diagnosed may yet spawn an auto-repair version.
 */
export function lineagePollInterval(
  lineage: GeometryLineageResponse | undefined,
  intervalMs: number,
  now = Date.now(),
): number | false {
  const versions = lineage?.versions;
  if (!versions?.length) return false;
  if (versions.some((v) => statusInProgress('geometry', v.status)))
    return intervalMs;
  const latest = versions.at(-1)!;
  if (latest.status !== 'idle' || latest.repair_result) return false;
  const since = Date.parse(latest.diagnosis_at ?? latest.created_at);
  return now - since < SETTLE_MS ? intervalMs : false;
}

const IN_FLIGHT: ReadonlySet<MeshResponse['status']> = new Set([
  'pending',
  'meshing',
  'recombining',
]);

/** The latest completed mesh, or null. `meshes` is newest-first (items[0] is newest). */
export function chosenMeshFor(meshes: MeshResponse[]): MeshResponse | null {
  return meshes.find((m) => m.status === 'completed') ?? null;
}

export function geometryMeshingStatusFor(
  meshes: MeshResponse[],
): GeometryMeshingStatus {
  if (meshes.length === 0) return 'not_started';
  if (meshes.some((m) => m.status === 'completed')) return 'completed';
  const latest = meshes[0];
  if (IN_FLIGHT.has(latest.status)) return 'meshing';
  if (latest.status === 'failed' || latest.status === 'cancelled')
    return 'failed';
  return 'not_started';
}

/**
 * The Mesh step's state for one geometry: its meshes first — a completed or
 * running mesh says everything — then what the geometry itself is doing,
 * then the mesh list's own verdict.
 */
export function geometryReadinessFor(args: {
  meshes: MeshResponse[];
  meshesLoaded: boolean;
  geometryStatus: GeometryStatus | undefined;
  geometryLoaded: boolean;
}): GeometryMeshingStatus {
  const { meshes, meshesLoaded, geometryStatus, geometryLoaded } = args;
  const fromMeshes = geometryMeshingStatusFor(meshes);
  if (fromMeshes === 'completed' || fromMeshes === 'meshing') return fromMeshes;
  if (!meshesLoaded || (!geometryLoaded && geometryStatus == null))
    return 'loading';
  if (geometryStatus == null) return fromMeshes;
  if (statusInProgress('geometry', geometryStatus)) return 'processing';
  if (geometryStatus === 'awaiting_approval') return 'awaiting_approval';
  if (geometryStatus === 'error') return 'geometry_error';
  return fromMeshes;
}
